// Which local draw a live collaboration session writes into, and what happens
// to it when the session ends. Fork-only: there is no upstream counterpart,
// because upstream keeps a single global scene and has nothing to choose
// between.
//
// The problem this solves: the room's scene has to be saved somewhere, and
// before this it went into whichever draw happened to be open. A person who
// joined by link therefore had somebody else's drawing written over their own
// work, and the room's retain policy had no safe way to say "remove the
// drawing this session created" afterwards, because nothing recorded which
// drawing that was.
//
// Plain functions, no state of its own: the state lives in the store (the
// marker on the document) and in `useCollabSessionDoc` (the running session).

import {
  createDoc,
  deleteDoc,
  findDocByCollabRoom,
  getDocMeta,
  setDocCollabRoom,
} from "../storage/multiFileStore";

import type { CollabEntryMode } from "./collabSession";
import type { DocCollabOrigin } from "../storage/types";

/** The draw one running collaboration session writes into. */
export interface CollabSessionDoc {
  roomId: string;
  docId: string;
  origin: DocCollabOrigin;
}

/**
 * Decide which local draw the session's scene belongs in, and mark it.
 *
 * Three cases, in the order they are tried:
 *
 *  1. A draw already carries this room's marker. That is a reload back into
 *     the same room (the documented way to rejoin one), so it is reused. Doing
 *     otherwise would leave one abandoned draw behind per reload, and would
 *     take the person who opened the room away from their own drawing.
 *  2. Opening a room. The user is sharing a drawing they already have, so it
 *     is only marked, never created. `origin: "shared"` is what later tells
 *     the discard path to leave it alone.
 *  3. Joining a room by link. The scene about to arrive is not this user's, so
 *     it gets a draw of its own. `origin: "created"` is written on a document
 *     this function created moments earlier, and nowhere else.
 *
 * @returns the session's draw, or `null` when opening a room with no active
 *   draw to share (nothing is marked in that case).
 */
export const openCollabSessionDoc = async (args: {
  roomId: string;
  entryMode: CollabEntryMode;
  activeDocId: string | null;
  docName: string;
}): Promise<CollabSessionDoc | null> => {
  const { roomId, entryMode, activeDocId, docName } = args;

  const existing = await findDocByCollabRoom(roomId);
  if (existing?.collabRoom) {
    return {
      roomId,
      docId: existing.id,
      origin: existing.collabRoom.origin,
    };
  }

  if (entryMode === "open") {
    if (!activeDocId) {
      return null;
    }
    await setDocCollabRoom(activeDocId, { roomId, origin: "shared" });
    return { roomId, docId: activeDocId, origin: "shared" };
  }

  const created = await createDoc(docName);
  await setDocCollabRoom(created.id, { roomId, origin: "created" });
  return { roomId, docId: created.id, origin: "created" };
};

/**
 * Remove the draw a joined session created, and only that draw.
 *
 * Three independent conditions all have to hold before anything is deleted:
 *
 *  - the session in hand says it created this draw (`origin: "created"`),
 *  - the stored document still carries a collaboration marker for the exact
 *    same room,
 *  - and that stored marker also says `"created"`.
 *
 * The marker is written by `openCollabSessionDoc` alone, and only on the
 * document it has just created itself, so a draw that existed before the
 * session cannot be carrying it. A draw shared by the person who opened the
 * room carries `"shared"` and is refused here by both the first and the third
 * condition.
 *
 * @returns whether a document was actually deleted.
 */
export const discardCollabSessionDoc = async (
  session: CollabSessionDoc,
): Promise<boolean> => {
  if (session.origin !== "created") {
    return false;
  }
  const meta = await getDocMeta(session.docId);
  if (
    meta?.collabRoom?.roomId !== session.roomId ||
    meta.collabRoom.origin !== "created"
  ) {
    return false;
  }
  await deleteDoc(session.docId);
  return true;
};

/**
 * Take this room's marker back off a draw that is being kept.
 *
 * Runs at the end of every session that does not delete its draw, so a marker
 * left over from a finished room can never authorise a later delete, and so a
 * kept draw goes back to being an ordinary one.
 */
export const clearCollabSessionDoc = (
  session: CollabSessionDoc,
): Promise<void> => setDocCollabRoom(session.docId, null, session.roomId);
